import { AUDIT_BASE_URL } from "../../utils/ApiUrls";

const AUDIT_SECTION_BASE_URL = `${AUDIT_BASE_URL}/sections`

class AuditSectionService {
  /**
   * Create a new audit section
   * @param {string} accessToken - JWT access token
   * @param {Object} sectionData - The audit section data (must include audit_plan_id)
   */
  async createAuditSection(accessToken, sectionData) {
    try {
      if (!sectionData.audit_plan_id) {
        return {
          success: false,
          status: 400,
          error: 'Audit plan ID is required',
          errorData: null,
        };
      }

      const response = await fetch(`${AUDIT_BASE_URL}/plans/${sectionData.audit_plan_id}/sections/create/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify(sectionData),
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          status: response.status,
          error: data.detail || 'Audit section creation failed',
          errorData: data,
        };
      }

      return {
        success: true,
        status: response.status,
        data,
      };
    } catch (error) {
      return {
        success: false,
        status: 0,
        error: error.message || 'Network error occurred',
        errorData: null,
      };
    }
  }

  /**
   * Fetch all audit sections for a specific audit plan with filtering and pagination
   * @param {string} accessToken - JWT access token
   * @param {number} auditPlanId - The audit plan ID (required)
   * @param {Object} filters - Filter options
   * @param {Object} paginationData - Pagination options
   */
  async getAuditSections(accessToken, auditPlanId, filters = {}, paginationData = {}) {
    try {
      if (!auditPlanId) {
        return {
          success: false,
          status: 400,
          error: 'Audit plan ID is required',
          errorData: null,
        };
      }

      // Build query parameters
      const queryParams = new URLSearchParams();
      
      if (filters.search) {
        queryParams.append('search', filters.search);
      }
      if (filters.ordering) {
        queryParams.append('ordering', filters.ordering);
      }

      if (paginationData.page) {
        queryParams.append('page', paginationData.page);
      }

      if (paginationData.page_size) {
        queryParams.append('page_size', paginationData.page_size);
      }

      const queryString = queryParams.toString();
      const url = `${AUDIT_BASE_URL}/plans/${auditPlanId}/sections/${queryString ? `?${queryString}` : ''}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          status: response.status,
          error: data.detail || 'Error fetching audit sections',
          errorData: data,
        };
      }

      return {
        success: true,
        status: response.status,
        data,
      };
    } catch (error) {
      return {
        success: false,
        status: 0,
        error: error.message || 'Network error occurred',
        errorData: null,
      };
    }
  }

  /**
   * Get detailed information about a specific audit section
   * @param {string} accessToken - JWT access token
   * @param {number} auditPlanId - The audit plan ID (required)
   * @param {number} sectionId - The audit section ID
   */
  async getAuditSectionDetail(accessToken, auditPlanId, sectionId) {
    try {
      if (!auditPlanId) {
        return {
          success: false,
          status: 400,
          error: 'Audit plan ID is required',
          errorData: null,
        };
      }

      const response = await fetch(`${AUDIT_BASE_URL}/plans/${auditPlanId}/sections/${sectionId}/`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          status: response.status,
          error: data.detail || 'Error fetching audit section details',
          errorData: data,
        };
      }

      return {
        success: true,
        status: response.status,
        data,
      };
    } catch (error) {
      return {
        success: false,
        status: 0,
        error: error.message || 'Network error occurred',
        errorData: null,
      };
    }
  }

  /**
   * Update an existing audit section
   * @param {string} accessToken - JWT access token
   * @param {number} auditPlanId - The audit plan ID (required)
   * @param {number} sectionId - The audit section ID
   * @param {Object} sectionData - The updated audit section data
   */
  async updateAuditSection(accessToken, auditPlanId, sectionId, sectionData) {
    try {
      if (!auditPlanId) {
        return {
          success: false,
          status: 400,
          error: 'Audit plan ID is required',
          errorData: null,
        };
      }

      const response = await fetch(`${AUDIT_BASE_URL}/plans/${auditPlanId}/sections/${sectionId}/edit/`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify(sectionData),
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          status: response.status,
          error: data.detail || 'Audit section update failed',
          errorData: data,
        };
      }

      return {
        success: true,
        status: response.status,
        data,
      };
    } catch (error) {
      return {
        success: false,
        status: 0,
        error: error.message || 'Network error occurred',
        errorData: null,
      };
    }
  }
}

const auditSectionService = new AuditSectionService();
export default auditSectionService;